var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import React, { useMemo } from 'react';
import cx from 'classnames';
import { scaleLinear } from 'd3-scale';
import { useKeyDown } from '@parca/components';
import { isSearchMatch } from '@parca/functions';
import { selectBinaries, setHoveringNode, useAppDispatch, useAppSelector } from '@parca/store';
import useNodeColor from './useNodeColor';
import { nodeLabel } from './utils';
export var RowHeight = 26;
export var IcicleGraphNodes = React.memo(function IcicleGraphNodesNoMemo(_a) {
    var data = _a.data, strings = _a.strings, mappings = _a.mappings, locations = _a.locations, functions = _a.functions, x = _a.x, y = _a.y, xScale = _a.xScale, total = _a.total, totalWidth = _a.totalWidth, level = _a.level, path = _a.path, setCurPath = _a.setCurPath, curPath = _a.curPath, searchString = _a.searchString, compareMode = _a.compareMode;
    var binaries = useAppSelector(selectBinaries);
    var nodes = curPath.length === 0
        ? data
        : data.filter(function (d) {
            return d != null &&
                curPath[0] ===
                    nodeLabel(d, strings, mappings, locations, functions, binaries.length > 1);
        });
    return (_jsx("g", __assign({ transform: "translate(".concat(x, ", ").concat(y, ")") }, { children: nodes.map(function nodeMapper(d, i) {
            var start = nodes.slice(0, i).reduce(function (sum, d) { return sum + parseFloat(d.cumulative); }, 0);
            var xStart = xScale(start);
            return (_jsx(IcicleNode, { x: xStart, y: 0, totalWidth: totalWidth, height: RowHeight, path: path, setCurPath: setCurPath, level: level, curPath: curPath, data: d, strings: strings, mappings: mappings, locations: locations, functions: functions, total: total, xScale: xScale, searchString: searchString, compareMode: compareMode }, "node-".concat(level, "-").concat(i)));
        }) })));
});
var icicleRectStyles = {
    cursor: 'pointer',
    transition: 'opacity .15s linear',
};
var fadedIcicleRectStyles = {
    cursor: 'pointer',
    transition: 'opacity .15s linear',
    opacity: '0.5',
};
export var IcicleNode = React.memo(function IcicleNodeNoMemo(_a) {
    var x = _a.x, y = _a.y, height = _a.height, setCurPath = _a.setCurPath, curPath = _a.curPath, level = _a.level, path = _a.path, data = _a.data, strings = _a.strings, mappings = _a.mappings, locations = _a.locations, functions = _a.functions, total = _a.total, totalWidth = _a.totalWidth, xScale = _a.xScale, _b = _a.isRoot, isRoot = _b === void 0 ? false : _b, searchString = _a.searchString, compareMode = _a.compareMode;
    var binaries = useAppSelector(selectBinaries);
    var dispatch = useAppDispatch();
    var isShiftDown = useKeyDown().isShiftDown;
    var colorResult = useNodeColor({ data: data, compareMode: compareMode });
    var name = useMemo(function () {
        return isRoot
            ? 'root'
            : nodeLabel(data, strings, mappings, locations, functions, binaries.length > 1);
    }, [data, strings, mappings, locations, functions, isRoot, binaries]);
    var nextPath = path.concat([name]);
    var isFaded = curPath.length > 0 && name !== curPath[curPath.length - 1];
    var styles = isFaded ? fadedIcicleRectStyles : icicleRectStyles;
    var nextLevel = level + 1;
    var cumulative = parseFloat(data.cumulative);
    var nextCurPath = curPath.length === 0 ? [] : curPath.slice(1);
    var newXScale = nextCurPath.length === 0 && curPath.length === 1
        ? scaleLinear().domain([0, cumulative]).range([0, totalWidth])
        : xScale;
    var width = nextCurPath.length > 0 || (nextCurPath.length === 0 && curPath.length === 1)
        ? totalWidth
        : xScale(cumulative);
    var _c = useMemo(function () {
        if (searchString === undefined || searchString === '') {
            return { isHighlightEnabled: false };
        }
        return { isHighlightEnabled: true, isHighlighted: isSearchMatch(searchString, name) };
    }, [searchString, name]), _d = _c.isHighlightEnabled, isHighlightEnabled = _d === void 0 ? false : _d, _e = _c.isHighlighted, isHighlighted = _e === void 0 ? false : _e;
    if (width <= 1) {
        return _jsx(_Fragment, { children: null });
    }
    var onMouseEnter = function () {
        if (isShiftDown)
            return;
        dispatch(setHoveringNode(data));
    };
    var onMouseLeave = function () {
        if (isShiftDown)
            return;
        dispatch(setHoveringNode(undefined));
    };
    return (_jsxs(_Fragment, { children: [_jsxs("g", __assign({ transform: "translate(".concat(x + 1, ", ").concat(y + 1, ")"), style: styles, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, onClick: function () {
                    setCurPath(nextPath);
                } }, { children: [_jsx("rect", { x: 0, y: 0, width: width, height: height, style: {
                            fill: colorResult,
                        }, className: cx('stroke-white dark:stroke-gray-700', {
                            'opacity-50': isHighlightEnabled && !isHighlighted,
                        }) }), width > 5 && (_jsx("svg", __assign({ width: width - 5, height: height }, { children: _jsx("text", __assign({ x: 5, y: 15, style: { fontSize: '12px' } }, { children: name })) })))] })), data.children !== undefined && data.children.length > 0 && (_jsx(IcicleGraphNodes, { data: data.children, strings: strings, mappings: mappings, locations: locations, functions: functions, x: x, y: RowHeight, xScale: newXScale, total: total, totalWidth: totalWidth, level: nextLevel, path: nextPath, curPath: nextCurPath, setCurPath: setCurPath, searchString: searchString, compareMode: compareMode }))] }));
});
